/**
 * Тонкий типизированный клиент read-API.
 *
 * Транспортная истина — типы, сгенерированные из OpenAPI (`generated.ts`).
 * Здесь нет ни одной руками написанной формы ответа: несовместимое изменение
 * контракта становится ошибкой `tsc`, а не сюрпризом в работе.
 *
 * Ошибка сервера приходит одним конвертом `{error:{code,message,details}}` —
 * клиент разбирает её одним типом и не угадывает форму по статусу.
 */

import { defineMessages } from "../i18n/define";
import { tr } from "../i18n/runtime";
import { localizeServerPayload, srv } from "../i18n/serverText";
import { bumpDataEpoch, setVersionConfirmer } from "./dataEpoch";
import type { components, paths } from "./generated";

export type Schemas = components["schemas"];

/**
 * Тексты ошибок самого клиента — те, что приходят не конвертом сервера, а от
 * транспорта. Экран, который печатает `error.message` напрямую, показывал их
 * по-русски на любом языке; код ошибки (`describeApiError`) этого не лечит там,
 * где код не разбирают. Поэтому они на языке интерфейса уже здесь.
 */
const m = defineMessages({
  aborted: {
    id: "common.apiClient.aborted",
    defaultMessage: "Запрос отменён",
    description: "Текст ошибки клиента: запрос прервал сам интерфейс (уход с экрана, смена фильтра)",
  },
  unreachable: {
    id: "common.apiClient.unreachable",
    defaultMessage: "Сервер недоступен. Проверьте, что backend запущен.",
    description: "Текст ошибки клиента: запрос не дошёл до сервера (сеть или сервер не запущен); backend — серверная часть программы",
  },
  statusReply: {
    id: "common.apiClient.statusReply",
    defaultMessage: "Сервер ответил {status}",
    description: "Текст ошибки клиента: сервер ответил отказом без своего конверта ошибки; {status} — числовой код ответа протокола, например 502",
  },
});

export type ErrorEnvelope = Schemas["ErrorEnvelope"];

/** Ошибка запроса: транспортная, HTTP или конверт сервера. */
export class ApiError extends Error {
  readonly code: string;
  readonly status: number;
  readonly details: Record<string, unknown> | null;

  constructor(
    code: string,
    message: string,
    status: number,
    details: Record<string, unknown> | null = null,
  ) {
    super(message);
    this.name = "ApiError";
    this.code = code;
    this.status = status;
    this.details = details;
  }
}

/**
 * Код отказа «объект вне области аккаунта». Взят ИЗ КОНТРАКТА, а не написан
 * строкой: сервер объявляет его перечислением, и переименование на сервере
 * обязано ломать сборку клиента, а не превращать понятный экран в белый.
 */
export const OUTSIDE_SCOPE: Schemas["ScopeErrorCode"] = "outside_scope";

/**
 * Код отказа «аккаунту не назначено ни одной роли». Приходит на закрытом
 * сервере (`AGROAI_READ_REQUIRES_LOGIN`) вошедшему аккаунту без ролей: вход
 * состоялся, области чтения нет.
 *
 * Тоже ИЗ КОНТРАКТА, как и `OUTSIDE_SCOPE`: сервер отдаёт этот код на тех же
 * маршрутах обоих семейств — выдачи и шлюза, — что объявили 403
 * перечислением, и до этой правки он в перечисление не входил — то есть
 * сервер отвечал кодом, которого его же схема не допускала, а клиенту
 * приходилось держать литерал.
 */
export const ROLE_REQUIRED: Schemas["ScopeErrorCode"] = "role_required";

/**
 * Во что экран разворачивает отказ. Не «ошибка вообще»: у этих случаев разные
 * слова и разные действия, а один заголовок «не загрузилось» на всё сразу
 * врёт про причину — особенно там, где сервер ответил осмысленно и быстро.
 *
 * `outside_scope` — сервер ответил, ответ понятен: это чужие данные;
 * `role_required` — вход состоялся, но роли аккаунту не назначено: чинится
 *   назначением роли, а не повтором запроса и не повторным входом;
 * `unauthenticated` — нужен вход;
 * `not_found` — такого объекта нет;
 * `aborted` — запрос отменил сам клиент (панорама карты), показывать нечего;
 * `offline` — до сервера не дошли;
 * `failed` — всё остальное.
 */
export type FailureKind =
  | "outside_scope"
  | "role_required"
  | "unauthenticated"
  | "not_found"
  | "aborted"
  | "offline"
  | "failed";

export function failureKind(error: ApiError): FailureKind {
  if (error.code === OUTSIDE_SCOPE) return "outside_scope";
  if (error.code === ROLE_REQUIRED) return "role_required";
  if (error.code === "aborted") return "aborted";
  if (error.code === "network_error") return "offline";
  if (error.status === 401) return "unauthenticated";
  if (error.status === 404) return "not_found";
  return "failed";
}

/** Отказ по области — тот самый 403, который экран показывает словами сервера. */
export function isOutsideScope(error: unknown): error is ApiError {
  return error instanceof ApiError && failureKind(error) === "outside_scope";
}

const BASE = (import.meta.env.VITE_API_BASE_URL ?? "/api/v1").replace(/\/$/, "");

type Primitive = string | number | boolean | null | undefined;

export type QueryParams = Record<string, Primitive>;

/**
 * Строка запроса — параметры ПО ИМЕНИ, в одном порядке (agroai-0e8.26,
 * журнал №1625). Сервер ключует готовый ответ строкой запроса как пришла
 * (`serving/cache.py`: порядок он за клиента не решает), а адаптеры собирают
 * один и тот же запрос в разном порядке — картина сезона из трёх мест
 * считалась бы трижды под тремя ключами. Порядок решает клиент, и один раз.
 */
function toSearch(params: QueryParams | undefined): string {
  if (!params) return "";
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === "") continue;
    search.set(key, String(value));
  }
  search.sort();
  const text = search.toString();
  return text ? `?${text}` : "";
}

function isEnvelope(value: unknown): value is ErrorEnvelope {
  return (
    typeof value === "object" &&
    value !== null &&
    "error" in value &&
    typeof (value as ErrorEnvelope).error?.code === "string"
  );
}

/**
 * Токен CSRF из cookie. Нужен любому небезопасному методу: вход в пилоте —
 * cookie-сессия, и без токена посторонняя страница смогла бы отправить отчёт
 * от имени вошедшего.
 */
function csrfToken(): string | null {
  const match = document.cookie.match(/(?:^|;\s*)csrftoken=([^;]*)/);
  const value = match?.[1];
  return value === undefined ? null : decodeURIComponent(value);
}

/** Сырой ответ сервера до разбора: его можно разделить между вызывающими. */
interface RawReply {
  ok: boolean;
  status: number;
  text: string;
}

/**
 * Одинаковые GET, ушедшие ОДНОВРЕМЕННО, — один запрос к серверу
 * (agroai-6al.44, журнал №441).
 *
 * Экраны Astra собраны из независимых блоков, и каждый спрашивает свои данные
 * сам: карточка хозяйства открывала `/actors/22` четыре раза подряд (739 КБ
 * каждый), картину сезона хозяйства — трижды. Общий запрос живёт только пока
 * он в пути: ответ НЕ запоминается, следующее открытие спросит сервер заново,
 * и свежесть данных не меняется. Делится только текст ответа — каждый
 * вызывающий разбирает его сам и получает свой объект, поэтому правка ответа
 * одним блоком не просочится в другой.
 *
 * Запрос с сигналом отмены или своими заголовками не делится: отмена одного
 * вызывающего не вправе оборвать ответ другому.
 */
const inflight = new Map<string, Promise<RawReply>>();

async function send(
  url: string,
  init: RequestInit | undefined,
  unsafe: boolean,
  token: string | null,
): Promise<RawReply> {
  let response: Response;
  try {
    response = await fetch(url, {
      // Cookie сессии обязана уезжать с запросом: dev-сервер проксирует `/api`
      // на Django, поэтому запрос остаётся однопроисхожденческим.
      credentials: "same-origin",
      ...init,
      headers: {
        Accept: "application/json",
        // Файл (multipart) границу ставит браузер — своего типа у него нет.
        ...(unsafe && !(init?.body instanceof FormData)
          ? { "Content-Type": "application/json" }
          : {}),
        ...(token ? { "X-CSRFToken": token } : {}),
        ...(init?.headers as Record<string, string> | undefined),
      },
    });
  } catch (cause) {
    // Отменённый запрос — не отказ сервера, а решение клиента: у него свой
    // код, чтобы вызывающий мог его молча проигнорировать и не показывать
    // пользователю ошибку там, где он просто подвинул карту.
    if (cause instanceof DOMException && cause.name === "AbortError") {
      throw new ApiError("aborted", tr(m.aborted), 0);
    }
    throw new ApiError(
      "network_error",
      tr(m.unreachable),
      0,
      { cause: String(cause) },
    );
  }
  return { ok: response.ok, status: response.status, text: await response.text() };
}

/* ------------------------------------------------------------------ */
/* Память ответов с проверкой версии данных (agroai-6al.54, №561)      */
/* ------------------------------------------------------------------ */
//
// Повторная навигация не должна заново качать и ждать то, что не менялось:
// карта района (3,6 МБ JSON), реестр контуров (20 страниц), картина сезона.
// Но и показать устаревшее как актуальное нельзя. Поэтому ответ запоминается
// ВМЕСТЕ с версией данных, под которой сервер его отдал (заголовок
// `X-Agroai-Version` — его ставят только кэшируемые маршруты), и отдаётся из
// памяти, ТОЛЬКО если сервер только что подтвердил ту же версию лёгким
// запросом `/data-version`. Версия — это отметка корпуса, дата, отпечаток кода
// и участник: сменилось хоть что-то — версия другая, и ответ берётся заново
// (браузер при этом спросит сервер с `If-None-Match` и получит 304, если сам
// ответ не изменился).
//
// Подтверждение действует в пределах одной навигации и не дольше
// `CONFIRM_MS`: экран спрашивает данные волнами (сначала цикл, потом картину
// цикла), и одна проверка на волну — это одна проверка на экран. Любая
// запись (POST и др.) подтверждение сбрасывает: после своего же отчёта
// пользователь не увидит прежнее число.

const VERSION_HEADER = "X-Agroai-Version";

/** Сколько живёт подтверждение версии внутри одной навигации, мс. */
export const CONFIRM_MS = 1500;

/** Предел памяти ответов — в символах текста (≈ вдвое больше в байтах). */
const REMEMBER_LIMIT = 40_000_000;

interface Remembered {
  text: string;
  version: string;
}

const remembered = new Map<string, Remembered>();
let rememberedSize = 0;

let confirmed: { version: string | null; at: number } | null = null;
let checking: Promise<string | null> | null = null;
let navigationKey: string | null = null;
/** Счётчик «подтверждение устарело»: навигация и любая запись его двигают. */
let epoch = 0;

function invalidateVersion(): void {
  epoch += 1;
  confirmed = null;
  checking = null;
  // Память разобранных ответов (адаптеры, корпус, геометрия) живёт не дольше
  // подтверждения версии: сброс — ровно там, где снимается подтверждение
  // (`api/dataEpoch.ts`, agroai-0e8.26, журнал №1626).
  bumpDataEpoch();
}

/** Новая навигация — прежнее подтверждение версии больше не действует. */
export function noteNavigation(key: string): void {
  if (key === navigationKey) return;
  navigationKey = key;
  invalidateVersion();
}

/** Забыть всё запомненное (выход, смена аккаунта, тесты). */
export function forgetRemembered(): void {
  remembered.clear();
  rememberedSize = 0;
  invalidateVersion();
}

function forget(url: string): void {
  const previous = remembered.get(url);
  if (previous) {
    rememberedSize -= previous.text.length;
    remembered.delete(url);
  }
}

function remember(url: string, entry: Remembered): void {
  forget(url);
  // Половина предела, а не четверть (agroai-0e8.26): раскрытие работ кабинета
  // (`/my/works/contours`, ~11 млн знаков) обязано помещаться — память
  // адаптеров теперь живёт одну навигацию, и без этой записи каждый переход
  // по кабинету качал бы его заново.
  if (entry.text.length > REMEMBER_LIMIT / 2) return;
  remembered.set(url, entry);
  rememberedSize += entry.text.length;
  // Вытесняется самое давнее: Map помнит порядок вставки, а чтение
  // переставляет запись в конец (см. `recall`).
  for (const [oldest, value] of remembered) {
    if (rememberedSize <= REMEMBER_LIMIT) break;
    remembered.delete(oldest);
    rememberedSize -= value.text.length;
  }
}

function recall(url: string): Remembered | undefined {
  const entry = remembered.get(url);
  if (entry) {
    remembered.delete(url);
    remembered.set(url, entry);
  }
  return entry;
}

async function fetchVersion(): Promise<string | null> {
  try {
    const response = await fetch(`${BASE}/data-version`, {
      credentials: "same-origin",
      cache: "no-store",
      headers: { Accept: "application/json" },
    });
    if (!response.ok) return null;
    const body = (await response.json()) as { version?: unknown };
    return typeof body.version === "string" ? body.version : null;
  } catch {
    return null;
  }
}

/** Текущая версия данных: одна проверка на волну запросов одной навигации. */
async function currentVersion(): Promise<string | null> {
  if (confirmed && Date.now() - confirmed.at < CONFIRM_MS) return confirmed.version;
  if (!checking) {
    const started = epoch;
    const pending = fetchVersion().then((version) => {
      // Ответ проверки, начатой до навигации или записи, ничего не
      // подтверждает: он про данные до неё.
      if (epoch === started) {
        confirmed = { version, at: Date.now() };
        checking = null;
      }
      return version;
    });
    checking = pending;
  }
  return checking;
}

// Память собранных наборов (`VersionedMemo`) сверяет версию тем же способом:
// одна проверка на волну запросов навигации (agroai-0e8.26, журнал №1638).
setVersionConfirmer(currentVersion);

async function sendRemembering(url: string): Promise<RawReply> {
  const entry = recall(url);
  if (entry) {
    const version = await currentVersion();
    if (version !== null && version === entry.version) {
      return { ok: true, status: 200, text: entry.text };
    }
  }
  let response: Response;
  try {
    response = await fetch(url, {
      credentials: "same-origin",
      headers: { Accept: "application/json" },
    });
  } catch (cause) {
    throw new ApiError(
      "network_error",
      tr(m.unreachable),
      0,
      { cause: String(cause) },
    );
  }
  const text = await response.text();
  const version = response.headers?.get?.(VERSION_HEADER) ?? null;
  if (response.ok && version) {
    remember(url, { text, version });
  } else if (!response.ok) {
    forget(url);
  }
  return { ok: response.ok, status: response.status, text };
}

/**
 * Кто слушает «экран прочёл запоминаемый ответ»: опись страницы для
 * предзагрузки (`api/prefetch.ts`). Зовётся из `request` — то есть на чтение
 * ЭКРАНА (из сети или из памяти), а не на предзагрузку, которая идёт мимо
 * `request` и опись не пачкает. Отдаётся адрес и размер текста — не данные.
 */
const rememberListeners = new Set<(url: string, size: number) => void>();

export function onRemembered(listener: (url: string, size: number) => void): () => void {
  rememberListeners.add(listener);
  return () => {
    rememberListeners.delete(listener);
  };
}

/** Сколько GET сейчас в пути — предзагрузка в простое ждёт тишины. */
export function inflightCount(): number {
  return inflight.size;
}

/**
 * Предзагрузить адрес в память ответов (agroai-0e8.26, ADR-0020).
 *
 * Тот же путь, что у живого запроса: уже летящий запрос не дублируется, ответ
 * запоминается ВМЕСТЕ с версией, и экран покажет его только после того, как
 * сервер подтвердит ту же версию на его навигации. Предзагрузка поэтому не
 * может показать старое: она лишь кладёт в память кандидата на показ. Отказ
 * молчит — это догадка о будущем экране, а не его запрос.
 */
export function prefetchUrl(url: string): Promise<void> {
  return shared(url).then(
    () => undefined,
    () => undefined,
  );
}

/** Префикс адресов API — опись страницы хранит полные адреса. */
export const API_BASE = BASE;

function shared(url: string): Promise<RawReply> {
  const pending = inflight.get(url);
  if (pending) return pending;
  const fresh = sendRemembering(url);
  inflight.set(url, fresh);
  const release = () => {
    if (inflight.get(url) === fresh) inflight.delete(url);
  };
  fresh.then(release, release);
  return fresh;
}

export async function request<T>(
  path: string,
  params?: QueryParams,
  init?: RequestInit,
): Promise<T> {
  const method = (init?.method ?? "GET").toUpperCase();
  const unsafe = method !== "GET" && method !== "HEAD";
  const token = unsafe ? csrfToken() : null;
  const url = `${BASE}${path}${toSearch(params)}`;
  const shareable =
    method === "GET" && !init?.signal && !init?.headers && init?.body === undefined;
  if (unsafe) invalidateVersion();
  const response = shareable ? await shared(url) : await send(url, init, unsafe, token);
  // Запись меняет данные: подтверждённая до неё версия больше не верна —
  // ни до ответа (запрос в пути), ни после.
  if (unsafe) invalidateVersion();
  if (shareable && response.ok && remembered.has(url)) {
    for (const listener of rememberListeners) listener(url, response.text.length);
  }

  const text = response.text;
  let payload: unknown = null;
  if (text) {
    try {
      payload = JSON.parse(text);
    } catch {
      payload = null;
    }
  }

  if (!response.ok) {
    if (isEnvelope(payload)) {
      throw new ApiError(
        payload.error.code,
        // Отказ роли/входа сервер пишет шаблоном («… требует одной из ролей: …»):
        // словарь клиента знает его и на узбекском, незнакомый текст остаётся русским.
        srv(payload.error.message),
        response.status,
        (payload.error.details as Record<string, unknown> | null) ?? null,
      );
    }
    throw new ApiError(
      "http_error",
      tr(m.statusReply, { status: response.status }),
      response.status,
    );
  }

  // Системные тексты сервера (подписи, этапы, оговорки) — на язык интерфейса.
  // Ответ сервера от языка не зависит; перевод — здесь, по словарю клиента.
  if (method === "GET") localizeServerPayload(path, payload);

  return payload as T;
}

/* ------------------------------------------------------------------ */
/* Именованные операции — единственная точка знания о путях API        */
/* ------------------------------------------------------------------ */

type Json<P extends keyof paths> = paths[P] extends {
  get: { responses: { 200: { content: { "application/json": infer R } } } };
}
  ? R
  : never;

/**
 * То же для POST. Отдельный тип, а не параметр у `Json`, потому что читающих
 * маршрутов в API подавляющее большинство, и объединение двух методов в одном
 * помощнике сделало бы каждый тип выдачи объединением «ответ GET либо ответ
 * POST» — то есть хуже читаемым ради трёх маршрутов шлюза.
 */
type PostJson<P extends keyof paths> = paths[P] extends {
  post: { responses: { 200: { content: { "application/json": infer R } } } };
}
  ? R
  : never;

export type RegistrySummary = Json<"/api/v1/registry/summary">;
export type MassifList = Json<"/api/v1/massifs">;
export type MassifDetail = Json<"/api/v1/massifs/{massif_id}">;
export type ContourList = Json<"/api/v1/contours">;
export type ContourIndex = Json<"/api/v1/contours/index">;
export type ContourIndexRow = Schemas["ContourIndexRow"];
export type ContourCycleOut = Schemas["ContourCycleOut"];
export type ContourDetail = Json<"/api/v1/contours/{contour_id}">;
export type ActorList = Json<"/api/v1/actors">;
export type ActorDetail = Json<"/api/v1/actors/{actor_id}">;
export type IssueList = Json<"/api/v1/data-quality/issues">;
export type MapReference = Json<"/api/v1/map/reference">;
export type MapContours = Json<"/api/v1/map/contours">;
export type MapCoverage = Json<"/api/v1/map/coverage">;
export type MapPlacementCoverage = Json<"/api/v1/map/placement-coverage">;
export type QuarantinePrevention = Json<"/api/v1/quarantine/prevention">;
export type QuarantineProblems = Json<"/api/v1/quarantine/problems">;
export type QuarantineStatuses = Json<"/api/v1/quarantine/statuses">;
export type MyWorks = Json<"/api/v1/my/works">;
export type MyWorkContours = Json<"/api/v1/my/works/contours">;
export type FarmObligationDetail =
  Json<"/api/v1/farm-obligations/{farm_obligation_id}">;
export type CampaignList = Json<"/api/v1/campaigns">;
export type VerificationQueue = Json<"/api/v1/verification/queue">;
export type CampaignDetail = Json<"/api/v1/campaigns/{campaign_id}/progress">;
export type Principal = Json<"/api/v1/auth/me">;
export type AgrocycleCatalog = Json<"/api/v1/agrocycle/catalog">;
export type AgrocycleTemplates = Json<"/api/v1/agrocycle/templates">;
export type AgrocycleTemplateDetail =
  Json<"/api/v1/agrocycle/templates/{template_id}">;
export type AgrocycleCycles = Json<"/api/v1/agrocycle/cycles">;
export type AgrocycleCycleDetail = Json<"/api/v1/agrocycle/cycles/{cycle_id}">;
export type AgrocycleCandidates = Json<"/api/v1/agrocycle/candidates">;
export type DistrictOverview = Json<"/api/v1/overview/district">;

/* -- сезон и ситуации: один срез, два вопроса ------------------------ */
//
// «Картина сезона» отвечает на «что сейчас в поле», очередь ситуаций — на «к
// кому ехать». Считаются они ОДНИМ обходом на сервере, поэтому и здесь стоят
// рядом: два экрана, разошедшиеся в числах, означали бы два разных среза.
export type SeasonPicture = Json<"/api/v1/season/picture">;
// Список районных циклов «культура × сезон». Отдельный маршрут, а не поле
// картины: картина отвечает про ОДИН срез, список — про ось, по которой
// срез выбирают, и спрашивают их разные экраны.
export type SeasonCycles = Json<"/api/v1/season/cycles">;
export type SeasonCycleRow = Schemas["SeasonCycleOut"];
// Статус обязательств по контурам (слой карты «Статус работы») и разбор
// хозяйства по типу сигнала — 6al.2.
export type SeasonContourStatus = Json<"/api/v1/season/contour-status">;
export type SeasonFarmBreakdown = Json<"/api/v1/season/farm-breakdown">;
export type SeasonBreakdownRow = Schemas["SeasonBreakdownRowOut"];
export type SeasonCampaignWork = Schemas["SeasonCampaignWorkOut"];
export type SituationList = Json<"/api/v1/situations">;
export type SituationDetail = Json<"/api/v1/situations/{key}">;
export type SituationDiff = Json<"/api/v1/situations/{key}/diff">;
export type SituationDisposition = PostJson<"/api/v1/situations/{key}/notice">;

export type SituationRow = Schemas["SituationOut"];
export type AssignmentList = Json<"/api/v1/assignments">;
export type AssignmentDiff = Json<"/api/v1/assignments/{assignment_id}/diff">;
export type AssignmentRecord = PostJson<"/api/v1/assignments/issue">;
export type AssignmentRow = Schemas["AssignmentOut"];
export type AssignmentStep = "start" | "report" | "check";
export type AssignmentCreateIn = Schemas["AssignmentCreateIn"];
export type SituationLens = Schemas["SituationLens"];
export type SituationLensCount = Schemas["SituationLensCountOut"];
export type SituationGround = Schemas["SituationGroundOut"];
export type SituationNote = Schemas["SituationNoteOut"];
export type SituationDispositionBrief = Schemas["SituationDispositionBriefOut"];
export type SituationQuantityChange = Schemas["SituationQuantityChangeOut"];
export type OpenCampaign = Schemas["OpenCampaignOut"];

/* -- AI-слой: каталог, ответ, план, журнал --------------------------- */
export type AiToolCatalog = Json<"/api/v1/ai/tools">;
export type AiAnswerList = Json<"/api/v1/ai/answers">;
export type AiAnswerRecord = Json<"/api/v1/ai/answers/{record_id}">;
export type AiEnvelope = PostJson<"/api/v1/ai/answer">;
export type AiPlan = PostJson<"/api/v1/ai/plan">;
export type AiAnswerPlan = PostJson<"/api/v1/ai/answer-plan">;

export type AiToolItem = Schemas["ToolCatalogItem"];
export type AiToolParam = Schemas["ToolParamOut"];
export type AiAnswerOk = Schemas["AnswerOk"];
/** Использовалась ли языковая модель — и если да, чем и по какой политике. */
export type AiModelUse = NonNullable<AiAnswerOk["model"]>;
export type AiAbstention = Schemas["Abstention"];
export type AiRejected = Schemas["RejectedEnvelope"];
export type AiAnswerSpec = Schemas["AnswerSpec"];
export type AiResolvedAnswer = Schemas["ResolvedAnswer"];
export type AiResolvedValue = NonNullable<AiResolvedAnswer["values"]>[string];
export type AiPhrase = Schemas["Phrase"];
export type AiValueRef = Schemas["ValueRef"];
export type AiBlock = NonNullable<AiAnswerSpec["blocks"]>[number];
export type AiQuestionPlan = Schemas["QuestionPlan"];
export type AiPlannedCall = Schemas["PlannedCall"];
export type AiAnswerListItem = Schemas["AnswerListItemOut"];
export type AiReadiness = Schemas["Readiness"];
export type AiAbstentionReason = Schemas["AbstentionReason"];

/* -- вода: журнал заявок, баланс и разложение по работе --------------- */
//
// Три разных вопроса и три ответа: «что просили и что пришло» (журнал),
// «сходится ли в выбранной области» (баланс) и «как подача легла на контуры
// одной работы» (работа). Одним ответом это были бы три знаменателя под одним
// именем — ровно то, что запрещает инвариант «две оси».
export type WaterLedger = Json<"/api/v1/water/ledger">;
export type WaterBalanceReply = Json<"/api/v1/water/balance">;
export type WaterWork = Json<"/api/v1/water/works/{work_def_id}">;
export type WaterSummary = Json<"/api/v1/water/summary">;
export type WaterSummaryRow = Schemas["WaterSummaryRowOut"];
export type WaterRequestRow = Schemas["WaterRequestRowOut"];
export type WaterBalance = Schemas["WaterBalanceOut"];
export type WaterAllocationRow = Schemas["WaterAllocationOut"];
export type WaterDeliveryRow = Schemas["WaterDeliveryOut"];
export type WaterFarmRow = Schemas["WaterFarmRowOut"];
export type WaterChannelRow = Schemas["WaterChannelRowOut"];
export type WaterShortfall = Schemas["WaterShortfallOut"];
export type WaterCycle = Json<"/api/v1/water/cycle">;

/* -- вода по файлу ирригации (С4–С5): район, хозяйство, разбор -------- */
//
// Факт полива приходит только из файла ирригации. Числа — слой метрик и
// функции сервера; экраны ничего не складывают. Хоким читает, менеджер
// загружает и разбирает, фермер видит свои поливы и может оспорить.
export type WaterDistrict = Json<"/api/v1/water/district">;
export type WaterMetric = Schemas["WaterMetricOut"];
export type FarmIrrigations = Json<"/api/v1/farms/{actor_id}/water/irrigations">;
export type FarmIrrigation = Schemas["WaterFarmIrrigationOut"];
export type IrrigationEvent = Schemas["WaterIrrigationEventOut"];
export type WaterDisputes = Json<"/api/v1/water/disputes">;
export type WaterDisputeRow = Schemas["WaterDisputeRowOut"];
export type WaterDispute = Schemas["WaterDisputeOut"];
export type WaterReportVersions = Json<"/api/v1/water/report-versions">;
export type WaterReportHistoryRow = Schemas["WaterReportVersionRowOut"];
export type WaterReview = Json<"/api/v1/water/review">;
export type WaterReviewEvent = Schemas["WaterReviewEventOut"];
export type WaterEventDetail = Json<"/api/v1/water/events/{event_id}">;
export type WaterReportVersion = Schemas["ReportVersionOut"];
export type WaterReportDetail = Json<"/api/v1/water/reports/{version_id}">;
export type WaterReportRow = Schemas["ReportRowOut"];
export type WaterCanals = Json<"/api/v1/water/canals">;

export type AnalysisContextOut = Schemas["AnalysisContextOut"];
export type ScopeOut = Schemas["ScopeOut"];
export type ScopeErrorEnvelope = Schemas["ScopeErrorEnvelope"];
export type ContourRow = Schemas["ContourRow"];
export type ActorRow = Schemas["ActorRow"];
export type IssueOut = Schemas["IssueOut"];
export type AreaOut = Schemas["AreaOut"];
export type CategoryTotal = Schemas["CategoryTotal"];
export type LandUseOut = Schemas["LandUseOut"];
export type MassifOut = Schemas["MassifOut"];
export type MassifCampaignOut = Schemas["MassifCampaignOut"];
export type MassifQuarantineOut = Schemas["MassifQuarantineOut"];
export type SourceRef = Schemas["SourceRef"];
export type SourceExcerpt = Schemas["SourceExcerpt"];
export type DirectionOut = Schemas["DirectionOut"];
export type PlacementBlock = Schemas["PlacementBlock"];
export type ActorLandUseOut = Schemas["ActorLandUseOut"];
export type ContourFeature = Schemas["ContourFeature"];
export type ContourFeatureProps = Schemas["ContourFeatureProps"];
export type ReferenceFeature = Schemas["ReferenceFeature"];
export type CoverageMassif = Schemas["CoverageMassif"];
export type CoverageTier = Schemas["CoverageTier"];
export type PlacementDetailBlock = Schemas["PlacementDetailBlock"];
export type PlacementDetailOut = Schemas["PlacementDetailOut"];
export type PlacementDetailCrop = Schemas["PlacementDetailCrop"];
export type PlacementCoverageCrop = Schemas["PlacementCoverageCrop"];
export type CropTimelineBlock = Schemas["CropTimelineBlock"];
export type CropOccupancyOut = Schemas["CropOccupancyOut"];
export type DemoBatchOut = Schemas["DemoBatchOut"];
export type QCampaignOut = Schemas["QCampaignOut"];
export type QContourBlock = Schemas["QContourBlock"];
export type QStatusRow = Schemas["QStatusRow"];
export type QStatusIntervalOut = Schemas["QStatusIntervalOut"];
export type QInspectionOut = Schemas["QInspectionOut"];
export type QFindingOut = Schemas["QFindingOut"];
export type ProgressCount = Schemas["ProgressCount"];
export type ProgressArea = Schemas["ProgressArea"];
export type PrincipalOut = Schemas["PrincipalOut"];
export type ActorScopeOut = Schemas["ActorScopeOut"];
export type FarmObligationOut = Schemas["FarmObligationOut"];
export type ObligationContourOut = Schemas["ObligationContourOut"];
export type WorkNormOut = Schemas["WorkNormOut"];
export type WorkPlaceOut = Schemas["WorkPlaceOut"];
export type WorkEntryOut = Schemas["WorkEntryOut"];
export type WorkVisitOut = Schemas["WorkVisitOut"];
export type ViewFiltersOut = Schemas["ViewFiltersOut"];
export type CampaignRow = Schemas["CampaignRow"];
export type CampaignProgressOut = Schemas["CampaignProgressOut"];
export type CampaignFarmOut = Schemas["CampaignFarmOut"];
export type BulkReportOut = Schemas["BulkReportOut"];
export type ContourReportOut = Schemas["ContourReportOut"];
export type VerificationQueueRowOut = Schemas["VerificationQueueRowOut"];
export type VerificationClaimOut = Schemas["VerificationClaimOut"];
export type TemplateBuilder = Schemas["BuilderOut"];
export type TemplateBuilderStage = Schemas["BuilderStageOut"];
export type TemplateBuilderWork = Schemas["BuilderWorkOut"];
export type TemplateLiveList = Schemas["TemplateLiveList"];
export type TemplateChangeList = Schemas["ChangeList"];
export type TemplateTimingIn = Schemas["TimingIn"];
export type TemplateBatchIn = Schemas["BatchIn"];
export type VerifyOut = Schemas["VerifyOut"];
export type BulkVerifyOut = Schemas["BulkVerifyOut"];
export type CatalogWorkOut = Schemas["CatalogWorkOut"];
export type TemplateRow = Schemas["TemplateRow"];
export type SlotOut = Schemas["SlotOut"];
export type CycleRow = Schemas["CycleRow"];
export type CandidateRow = Schemas["CandidateRow"];
export type SeasonGuardOut = Schemas["SeasonGuardOut"];
export type DeviationOut = Schemas["DeviationOut"];
export type AvailabilityEventOut = Schemas["AvailabilityEventOut"];
export type AttentionItemOut = Schemas["AttentionItemOut"];
export type AttentionClearOut = Schemas["AttentionClearOut"];
export type AttentionGapOut = Schemas["AttentionGapOut"];
export type LandCategoryOut = Schemas["LandCategoryOut"];
export type CropMetricOut = Schemas["CropMetricOut"];
export type CropStructureOut = Schemas["CropStructureOut"];
export type MassifRowOut = Schemas["MassifRowOut"];
export type MassifProgressOut = Schemas["MassifProgressOut"];
export type OverviewCampaignOut = Schemas["OverviewCampaignOut"];
export type InsightsOut = Schemas["InsightsOut"];
export type FindingOut = Schemas["FindingOut"];
export type SimilarFarmsOut = Schemas["SimilarFarmsOut"];

export const api = {
  registrySummary: (params: QueryParams) =>
    request<RegistrySummary>("/registry/summary", params),
  districtOverview: (params: QueryParams) =>
    request<DistrictOverview>("/overview/district", params),
  massifs: (params: QueryParams) => request<MassifList>("/massifs", params),
  /** Одна территория одним запросом: ход кампаний и карантин уже разделены
      сервером, поэтому экрану массива не нужен районный свод. */
  massif: (id: number, params: QueryParams) =>
    request<MassifDetail>(`/massifs/${id}`, params),
  contours: (params: QueryParams) => request<ContourList>("/contours", params),
  /** Справочник контуров среза одним ответом — вместо обхода `/contours` по 500 (agroai-0e8.26). */
  contoursIndex: (params: QueryParams) => request<ContourIndex>("/contours/index", params),
  contour: (id: number, params: QueryParams) =>
    request<ContourDetail>(`/contours/${id}`, params),
  actors: (params: QueryParams) => request<ActorList>("/actors", params),
  actor: (id: number, params: QueryParams) =>
    request<ActorDetail>(`/actors/${id}`, params),
  issues: (params: QueryParams) =>
    request<IssueList>("/data-quality/issues", params),
  mapReference: (params: QueryParams, signal?: AbortSignal) =>
    request<MapReference>("/map/reference", params, { signal }),
  // Единственный запрос, который клиент отменяет: при панораме и зуме
  // предыдущий ответ уже не нужен, а он самый тяжёлый в API.
  mapContours: (params: QueryParams, signal?: AbortSignal) =>
    request<MapContours>("/map/contours", params, { signal }),
  mapCoverage: (params: QueryParams, signal?: AbortSignal) =>
    request<MapCoverage>("/map/coverage", params, { signal }),
  // Покрытие размещения синтетической детализацией: фактические колонки и
  // демо-колонки в одном ответе, но раздельно — так их и показывать.
  mapPlacementCoverage: (params: QueryParams, signal?: AbortSignal) =>
    request<MapPlacementCoverage>("/map/placement-coverage", params, { signal }),
  // Карантин — два режима, два запроса. Один эндпоинт с флагом позволил бы
  // экрану считать охват профилактики «покрытием статусов»; спецификация это
  // запрещает, и разделение живёт уже в контракте.
  quarantineProblems: () => request<QuarantineProblems>("/quarantine/problems"),
  quarantinePrevention: (params: QueryParams) =>
    request<QuarantinePrevention>("/quarantine/prevention", params),
  quarantineStatuses: (params: QueryParams) =>
    request<QuarantineStatuses>("/quarantine/statuses", params),

  /* -- вход и права ------------------------------------------------- */
  //
  // Клиент по ответу /auth/me рисует кнопки, но НЕ принимает решение: право на
  // запись проверяет сервер в доменном сервисе. Спрятанная кнопка ничего не
  // защищает, поэтому и не считается защитой.
  me: (params: QueryParams) => request<Principal>("/auth/me", params),
  csrf: () => request<{ csrf_token: string }>("/auth/csrf"),
  login: (username: string, password: string, params: QueryParams) =>
    request<Principal>("/auth/login", params, {
      method: "POST",
      body: JSON.stringify({ username, password }),
    }),
  logout: (params: QueryParams) => {
    // Запомненные ответы прежнего аккаунта не показались бы и так (участник
    // входит в версию), но держать их в памяти общего компьютера незачем.
    forgetRemembered();
    return request<Principal>("/auth/logout", params, { method: "POST" });
  },

  /* -- работы: два входа в одну модель ------------------------------- */
  //
  // «Что от меня ждут» входит с уровня хозяйства, прогресс кампании — с уровня
  // района. Под ними одни и те же контурные факты: отдельной «упрощённой»
  // модели для хозяйства нет.
  myWorks: (params: QueryParams) => request<MyWorks>("/my/works", params),
  myWorksContours: (params: QueryParams) =>
    request<MyWorkContours>("/my/works/contours", params),
  farmObligation: (id: number, params: QueryParams) =>
    request<FarmObligationDetail>(`/farm-obligations/${id}`, params),
  campaigns: (params: QueryParams) => request<CampaignList>("/campaigns", params),
  campaignProgress: (id: number, params: QueryParams) =>
    request<CampaignDetail>(`/campaigns/${id}/progress`, params),

  /* -- третий вход: очередь проверяющего ----------------------------- */
  //
  // Вопрос этого экрана другой: не «что сделано», а «о чём заявлено и ещё не
  // разобрано». Данные под ним те же контурные факты — отдельного списка
  // «непроверенного» на сервере не заводится.
  verificationQueue: (params: QueryParams) =>
    request<VerificationQueue>("/verification/queue", params),

  /* -- сезон и ситуации ---------------------------------------------- */
  //
  // «Картина сезона» — один вызов вместо свёртки журнала работ в браузере.
  seasonPicture: (params: QueryParams) =>
    request<SeasonPicture>("/season/picture", params),
  // Список районных циклов «культура × сезон». Ключ цикла собран из пары и
  // едет строкой: идентификатора базы у цикла нет.
  seasonCycles: (params: QueryParams) =>
    request<SeasonCycles>("/season/cycles", params),
  // Строки агроциклов (ядро S4): участники, план, ход — с сервера.
  agrocycles: (params: QueryParams) =>
    request<Schemas["AgrocyclesOut"]>("/agrocycles", params),
  // Статус работы по контурам на дату: одна работа (`work_def_id`), стадия
  // (`stage`) или весь срез; хозяйства — словарём `actors`.
  seasonContourStatus: (params: QueryParams) =>
    request<SeasonContourStatus>("/season/contour-status", params),
  // Разбор хозяйства по типу сигнала: сроки / вода / расхождения / карантин;
  // раздел `null` — сигнала этого типа нет.
  seasonFarmBreakdown: (params: QueryParams) =>
    request<SeasonFarmBreakdown>("/season/farm-breakdown", params),

  /* -- вода ----------------------------------------------------------- */
  waterLedger: (params: QueryParams) =>
    request<WaterLedger>("/water/ledger", params),
  waterBalance: (params: QueryParams) =>
    request<WaterBalanceReply>("/water/balance", params),
  waterSummary: (group: "week" | "channel" | "actor" | "massif", params: QueryParams) =>
    request<WaterSummary>("/water/summary", { ...params, group }),
  waterWork: (workDefId: number, params: QueryParams) =>
    request<WaterWork>(`/water/works/${workDefId}`, params),
  // Вода цикла: подачи и расхождения сверки по его поливам (считает сервер).
  waterCycle: (params: QueryParams) => request<WaterCycle>("/water/cycle", params),

  /* -- вода по файлу ирригации: чтение ---------------------------------- */
  waterDistrict: (params: QueryParams) =>
    request<WaterDistrict>("/water/district", params),
  farmIrrigations: (actorId: number, params: QueryParams) =>
    request<FarmIrrigations>(`/farms/${actorId}/water/irrigations`, params),
  waterDisputes: (params: QueryParams) =>
    request<WaterDisputes>("/water/disputes", params),
  waterReportVersions: (params: QueryParams) =>
    request<WaterReportVersions>("/water/report-versions", params),
  waterReview: (params: QueryParams) => request<WaterReview>("/water/review", params),
  waterCanals: (params: QueryParams) => request<WaterCanals>("/water/canals", params),
  waterEvent: (eventId: number, params: QueryParams) =>
    request<WaterEventDetail>(`/water/events/${eventId}`, params),
  waterReportDetail: (versionId: number, params: QueryParams & { status?: string; limit?: number; offset?: number }) =>
    request<WaterReportDetail>(`/water/reports/${versionId}`, params),

  /* -- вода по файлу ирригации: запись (менеджер; фермер — только спор) -- */
  raiseWaterDispute: (
    body: { obligation_id: number; event_id?: number | null; note: string },
    params: QueryParams,
  ) =>
    request<WaterDispute>("/water/disputes", params, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  resolveWaterDispute: (id: number, note: string, params: QueryParams) =>
    request<WaterDispute>(`/water/disputes/${id}/resolve`, params, {
      method: "POST",
      body: JSON.stringify({ note }),
    }),
  chooseWaterEventCycle: (eventId: number, choice: { cycle_id: number } | { split: true }, params: QueryParams) =>
    request<WaterEventDetail>(`/water/events/${eventId}/cycle`, params, {
      method: "POST",
      body: JSON.stringify(choice),
    }),
  chooseQuarantineWorkCycle: (obligationId: number, cycleId: number) =>
    request<Schemas["QWorkCycleOut"]>(`/quarantine/works/${obligationId}/cycle`, undefined, {
      method: "POST",
      body: JSON.stringify({ cycle_id: cycleId }),
    }),
  assignWaterEvent: (eventId: number, obligationId: number, params: QueryParams) =>
    request<WaterEventDetail>(`/water/events/${eventId}/assign`, params, {
      method: "POST",
      body: JSON.stringify({ obligation_id: obligationId }),
    }),
  uploadWaterReport: (
    file: File,
    fields: { issuer?: string; period_from?: string; period_to?: string; adapter?: string },
    params: QueryParams,
  ) => {
    const form = new FormData();
    form.append("file", file);
    for (const [name, value] of Object.entries(fields)) if (value) form.append(name, value);
    return request<WaterReportVersion>("/water/reports", params, { method: "POST", body: form });
  },
  resolveWaterRow: (
    versionId: number,
    rowId: number,
    body: {
      contour_id?: number | null;
      canal_id?: number | null;
      save_alias?: boolean;
      ignore?: boolean;
      note?: string;
    },
    params: QueryParams,
  ) =>
    request<WaterReportRow>(`/water/reports/${versionId}/rows/${rowId}/resolve`, params, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  publishWaterReport: (versionId: number, params: QueryParams) =>
    request<WaterReportVersion>(`/water/reports/${versionId}/publish`, params, { method: "POST" }),
  rejectWaterReport: (versionId: number, params: QueryParams) =>
    request<WaterReportVersion>(`/water/reports/${versionId}/reject`, params, { method: "POST" }),
  // Очередь ситуаций. Линза едет параметром, а не отдельным маршрутом: это
  // ВЗГЛЯД на один и тот же срез, и счётчики соседних линз приходят тем же
  // ответом — иначе выбор следующей линзы был бы выбором вслепую.
  situations: (params: QueryParams) =>
    request<SituationList>("/situations", params),
  situation: (key: string, params: QueryParams) =>
    request<SituationDetail>(`/situations/${encodeURIComponent(key)}`, params),
  // «Что изменилось» — отдельный вызов, а не поле строки: сравнение считается
  // против снимка и стоит запроса, а строка очереди отвечает на вопрос «что
  // видно сегодня». Одним ответом это были бы две правды под одним именем.
  situationDiff: (key: string, params: QueryParams) =>
    request<SituationDiff>(
      `/situations/${encodeURIComponent(key)}/diff`,
      params,
    ),

  /* -- запись диспозиции по ситуации --------------------------------- */
  //
  // Три жеста и ни одного четвёртого: «заметили» фиксирует, что человек это
  // видит, «отреагировали» — что он сделал, «закрыли» — что здесь закончено.
  // Ни редактирования, ни отката: след решения не переписывается, исправление
  // выглядит следующим жестом со своими словами.
  //
  // Основания клиент НЕ присылает — их считает сервер тем же детектором, что
  // и показанная строка. Позволить прислать их значило бы разрешить нажавшему
  // кнопку сочинить, что он якобы увидел.
  noticeSituation: (key: string, comment: string, params: QueryParams) =>
    request<SituationDisposition>(
      `/situations/${encodeURIComponent(key)}/notice`,
      params,
      { method: "POST", body: JSON.stringify({ comment }) },
    ),
  respondSituation: (key: string, comment: string, params: QueryParams) =>
    request<SituationDisposition>(
      `/situations/${encodeURIComponent(key)}/respond`,
      params,
      { method: "POST", body: JSON.stringify({ comment }) },
    ),
  closeSituation: (key: string, comment: string, params: QueryParams) =>
    request<SituationDisposition>(
      `/situations/${encodeURIComponent(key)}/close`,
      params,
      { method: "POST", body: JSON.stringify({ comment }) },
    ),

  /* -- поручения (astra-on-canon, фаза 3) ----------------------------- */
  //
  // Чтение — выдача (список с журналом и следующим шагом, «что изменилось»
  // против снимка постановки); запись — приложение `assignments`. Основания
  // снимка клиент не присылает: их считает сервер тем же детектором, что
  // показал ситуацию, на срезе `params`.
  assignments: (params: QueryParams) =>
    request<AssignmentList>("/assignments", params),
  assignmentDiff: (id: number, params: QueryParams) =>
    request<AssignmentDiff>(`/assignments/${id}/diff`, params),
  issueAssignment: (body: AssignmentCreateIn, params: QueryParams) =>
    request<AssignmentRecord>("/assignments/issue", params, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  advanceAssignment: (id: number, step: AssignmentStep, comment: string) =>
    request<AssignmentRecord>(`/assignments/${id}/${step}`, undefined, {
      method: "POST",
      body: JSON.stringify({ comment }),
    }),

  /* -- сканер находок и похожие хозяйства (ADR-0014) ------------------- */
  //
  // Производный срез ночного сканера по слою метрик: находки (отклонения от
  // прошлого сезона и ровесников, связи семейств, смена темпа) и k ближайших
  // хозяйств по профилю метрик. Область режет сервер.
  insights: (params: QueryParams) => request<InsightsOut>("/insights", params),
  similarFarms: (actorId: number, params: QueryParams) =>
    request<SimilarFarmsOut>(`/insights/farms/${actorId}/similar`, params),

  /* -- запись отчётов ------------------------------------------------ */
  reportFarmObligation: (
    id: number,
    body: {
      progress: string;
      occurred_on?: string | null;
      area_basis?: string;
      note?: string;
    },
    params: QueryParams,
  ) =>
    request<BulkReportOut>(`/farm-obligations/${id}/report`, params, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  reportObligation: (
    id: number,
    body: {
      progress: string;
      occurred_on?: string | null;
      amend?: boolean;
      note?: string;
    },
    params: QueryParams,
  ) =>
    request<ContourReportOut>(`/obligations/${id}/report`, params, {
      method: "POST",
      body: JSON.stringify(body),
    }),

  /* -- запись проверок ------------------------------------------------ */
  //
  // Проверка ничего в отчёте не меняет: при расхождении сервер возвращает
  // состояние «конфликт», а обе записи остаются. Клиенту здесь нечего
  // «разрешать» — причина обязательна для спора, и отказывает сервер.
  verifyObligation: (
    id: number,
    body: {
      outcome: string;
      occurred_on?: string | null;
      note?: string;
      execution_id?: number | null;
    },
    params: QueryParams,
  ) =>
    request<VerifyOut>(`/obligations/${id}/verify`, params, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  verifyFarmObligation: (
    id: number,
    body: { outcome: string; occurred_on?: string | null; note?: string },
    params: QueryParams,
  ) =>
    request<BulkVerifyOut>(`/farm-obligations/${id}/verify`, params, {
      method: "POST",
      body: JSON.stringify(body),
    }),

  /* -- AI-слой: спросить инструментом, а не написать текст ------------- */
  //
  // Контекст анализа у трёх маршрутов шлюза стоит в РАЗНЫХ местах, и это не
  // недосмотр контракта: `/ai/answer` принимает его телом (там же, где
  // параметры вызова), а `/ai/plan` и `/ai/answer-plan` — параметрами запроса,
  // как любой GET выдачи. Клиент повторяет контракт, а не выравнивает его по
  // своему вкусу: выровняв, он посылал бы срез туда, где его не читают.
  aiTools: (params: QueryParams) =>
    request<AiToolCatalog>("/ai/tools", params),
  aiAnswer: (
    body: {
      tool_id: string;
      params: Record<string, string | number>;
      context: { as_of?: string | null; include_demo?: boolean };
      object_id?: number | null;
      /**
       * Уровень ЭКРАНА, с которого задан вопрос. Полномочием он НЕ является
       * (область — из сессии): это выбор селектора у инструмента,
       * объявленного сразу на нескольких уровнях (Решение Д11). Без него
       * сервер вынужден угадывать, к какому роду единицы относится
       * `object_id`: одно число не говорит, массив это или хозяйство, и
       * инструмент трёх уровней ответил бы районным итогом под адресом
       * хозяйства. Поле необязательно — без него сервер берёт уровень
       * объекта, а при его отсутствии — уровень области участника.
       */
      level?: Schemas["LevelId"] | null;
    },
    params: QueryParams,
  ) =>
    request<AiEnvelope>("/ai/answer", params, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  aiPlan: (
    body: { question: string; object_id?: number | null },
    params: QueryParams,
  ) =>
    request<AiPlan>("/ai/plan", params, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  aiAnswerPlan: (
    body: {
      plan: AiQuestionPlan;
      object_id?: number | null;
      question?: string;
    },
    params: QueryParams,
  ) =>
    request<AiAnswerPlan>("/ai/answer-plan", params, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  // История — журнал СВОИХ вопросов (районной области — весь район). Контекст
  // анализа ей не передаётся: журнал читают «сейчас», и срез прошлого не
  // открывает записи, которых сегодня видеть нельзя.
  aiAnswers: (params: QueryParams) =>
    request<AiAnswerList>("/ai/answers", params),
  aiAnswerRecord: (id: string) =>
    request<AiAnswerRecord>(`/ai/answers/${id}`),

  /* -- агроцикл -------------------------------------------------------- */
  //
  // Состояние работы клиент НЕ выводит: сервер отдаёт готовое `state`.
  // Правило «заблокирована → доступна → требуется → просрочена, а выполнено
  // возможно в любой момент» обязано быть одно на весь продукт — иначе экран
  // хокимията и экран хозяйства однажды назовут одну работу по-разному.
  agrocycleCatalog: (params: QueryParams) =>
    request<AgrocycleCatalog>("/agrocycle/catalog", params),
  agrocycleTemplates: (params: QueryParams) =>
    request<AgrocycleTemplates>("/agrocycle/templates", params),
  agrocycleTemplate: (id: number, params: QueryParams) =>
    request<AgrocycleTemplateDetail>(`/agrocycle/templates/${id}`, params),
  // Конструктор живого шаблона (ядро S3): запись несёт ревизию, которую видел
  // человек (`If-Match`); устаревшая — 409 `template_changed`.
  liveTemplates: (params: QueryParams) =>
    request<TemplateLiveList>("/agrocycle/live-templates", params),
  liveTemplate: (id: number, params: QueryParams) =>
    request<TemplateBuilder>(`/agrocycle/templates/${id}/live`, params),
  liveTemplateChanges: (id: number, params: QueryParams) =>
    request<TemplateChangeList>(`/agrocycle/templates/${id}/changes`, params),
  createTemplate: (
    body: { crop_id: number; name?: string; copy_from_id?: number | null },
    params: QueryParams,
  ) =>
    request<TemplateBuilder>("/agrocycle/templates", params, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  editTemplate: (
    id: number,
    revision: number,
    edit: {
      method: "POST" | "PATCH" | "DELETE";
      path: string;
      body?: unknown;
      query?: QueryParams;
    },
    params: QueryParams,
  ) =>
    request<TemplateBuilder>(
      `/agrocycle/templates/${id}/${edit.path}`,
      { ...params, ...edit.query },
      {
        method: edit.method,
        headers: { "If-Match": String(revision) },
        ...(edit.body === undefined ? {} : { body: JSON.stringify(edit.body) }),
      },
    ),
  createCatalogWork: (
    body: { name: string; category: string },
    params: QueryParams,
  ) =>
    request<Schemas["CatalogItemOut"]>("/agrocycle/catalog", params, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  agrocycleCycles: (params: QueryParams) =>
    request<AgrocycleCycles>("/agrocycle/cycles", params),
  agrocycleCycle: (id: number, params: QueryParams) =>
    request<AgrocycleCycleDetail>(`/agrocycle/cycles/${id}`, params),
  agrocycleCandidates: (params: QueryParams) =>
    request<AgrocycleCandidates>("/agrocycle/candidates", params),
  setSlotAvailability: (
    workDefId: number,
    body: {
      action: string;
      effective_on: string;
      reason?: string;
      cycle_id?: number | null;
    },
    params: QueryParams,
  ) =>
    request<AgrocycleTemplateDetail>(
      `/agrocycle/slots/${workDefId}/availability`,
      params,
      { method: "POST", body: JSON.stringify(body) },
    ),
  startAgrocycle: (
    body: { occupancy_id: number; on_date: string; note?: string },
    params: QueryParams,
  ) =>
    request<AgrocycleCycleDetail>("/agrocycle/cycles", params, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  finishAgrocycle: (id: number, body: { on_date: string }, params: QueryParams) =>
    request<AgrocycleCycleDetail>(`/agrocycle/cycles/${id}/finish`, params, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  deviateAgrocycle: (
    id: number,
    body: {
      kind: string;
      reason: string;
      decided_on: string;
      work_def_id?: number | null;
      catalog_work_id?: number | null;
      shift_from?: string | null;
      shift_to?: string | null;
    },
    params: QueryParams,
  ) =>
    request<AgrocycleCycleDetail>(`/agrocycle/cycles/${id}/deviations`, params, {
      method: "POST",
      body: JSON.stringify(body),
    }),
};
